import { computed } from "mobx";
import {
  ExtendedModel,
  isoStringToDateTransform,
  modelAction,
  tProp,
  types,
} from "mobx-keystone";
import { api } from "../../../orpc/client";
import { TModel } from "../../lib/models/t-model";
import { Currency } from "../enums";
import { invoiceNS } from "./ns.invoice";
import type { Self } from "../../lib/models/ref";

export type InvoiceStatusKind = "UNPAID" | "PAID" | "PARTIAL";

@invoiceNS.model
export class InvoiceModel extends ExtendedModel(TModel, {
  currency: tProp(types.enum(Currency)),
  externalId: tProp(types.string),
  amount: tProp(types.number),
  amountOpen: tProp(types.number),
  pdfURL: tProp(types.maybeNull(types.string)),
  note: tProp(types.maybeNull(types.string)),
  serviceAccountId: tProp(types.string),
  latePaymentAmount: tProp(types.number, 0),
  dueAt: tProp(types.string).withTransform(isoStringToDateTransform()),
  invoicedAt: tProp(types.string).withTransform(isoStringToDateTransform()),
}) {
  @computed
  get serviceAccount() {
    if (!this.detaching && this.root) {
      const account = this.root.tilliX.serviceAccounts.modelRef(
        this.serviceAccountId,
      );

      return account.maybeCurrent;
    }

    // biome-ignore lint/nursery/noUselessUndefined: <return undefined for getter>
    return undefined;
  }

  @computed
  get status() {
    return {
      overdue: this.dueAt < new Date(),
      paid: this.amountOpen <= 0,
      balance: this.amount - this.amountOpen,
    };
  }

  @computed
  get statusKind(): InvoiceStatusKind {
    // Negative-amount documents are SAP credit memos, which SAP itself reports
    // as cleared (InvoiceStatus "Y") — so they fall through to PAID and show
    // their real (negative) amount, same as any other settled invoice.
    if (this.amountOpen <= 0) return "PAID";
    if (this.amountOpen < this.amount) return "PARTIAL";
    return "UNPAID";
  }

  @computed
  get isSelectedForPayment(): boolean {
    if (this.detaching || !this.root) return false;
    return this.root.tilliX.invoices.selectedForPayment.some(
      (ref) => ref.maybeCurrent?.id === this.id,
    );
  }

  @computed
  get isSelectableForPayment(): boolean {
    return this.amountOpen > 0 && (this.serviceAccount?.balance ?? 0) > 0;
  }

  @modelAction
  protected _setValueForKey<K extends keyof this & keyof Self<this>>(
    key: K,
    value: Self<this>[K] | this[K],
  ): void {
    this[key] = value;
  }

  async getPdfBlob(): Promise<string> {
    const result = await api.invoice.pdf.get({ id: this.id });
    return result.pdfBlob;
  }
}
